import type { WireArticleFull } from '../../../api/types'
import { PortionMark } from '../common/PortionMark'
import { useTopics, useRegions, topicLabel, regionLabel } from '../../../hooks/useTaxonomy'

interface ArticleHeaderProps {
  article: WireArticleFull
}

/**
 * Article-level header rendered from WireArticleFull fields (not blocks).
 * Mirrors the mockup layout: classification strip → metadata strip → title → description/BLUF.
 */
export function ArticleHeader({ article }: ArticleHeaderProps) {
  const { data: regionData = [] } = useRegions()
  const { data: topicData = [] } = useTopics()

  return (
    <header className="space-y-3">
      {/* Metadata strip: regions · topics · date · read time */}
      <div className="flex flex-wrap items-center gap-2 article-metadata text-fg-secondary">
        {article.regions && article.regions.length > 0 && article.regions.map(slug => (
          <>
            <span key={slug}>{regionLabel(regionData, slug)}</span>
            <span aria-hidden="true">·</span>
          </>
        ))}
        {article.topics && article.topics.length > 0 && article.topics.map(slug => (
          <>
            <span key={slug}>{topicLabel(topicData, slug)}</span>
            <span aria-hidden="true">·</span>
          </>
        ))}
        {article.publishedAt && (
          <>
            <span>
              {new Date(article.publishedAt).toLocaleDateString('en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              }).toUpperCase()}
            </span>
            <span aria-hidden="true">·</span>
          </>
        )}
        {article.readTime !== undefined && (
          <span>{article.readTime} MIN READ</span>
        )}
      </div>

      {/* Title — with article-level portion mark */}
      <h1 className="article-title text-fg">
        <PortionMark classification={article.classification} disseminationControls={article.disseminationControls} />
        {article.title}
      </h1>

      {/* Description / BLUF */}
      {article.description && (
        <p className="article-description text-fg-secondary">
          <PortionMark classification={article.classification} disseminationControls={article.disseminationControls} />
          {article.description}
        </p>
      )}

      {/* Byline */}
      {article.byline && (
        <p className="article-byline text-fg-secondary">
          {article.byline}
        </p>
      )}
    </header>
  )
}
